import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

import { useRoutineProgress } from '../features/progress/useRoutineProgress';
import { useActiveProfile } from '../features/profiles/useActiveProfile';
import { getRewardEmoji } from '../features/progress/rewardEmoji';
import { playDing } from '../effects/ding';
import { flyStar } from '../effects/flyStar';
import { spawnSparkles } from '../effects/spawnSparkles';
import { StarShelf, type StarShelfHandle } from '../effects/StarShelf';
import { useSoundEnabled } from '../effects/useSoundEnabled';
import { useLongPress } from '../hooks/useLongPress';
import { bySortIndex } from '../lib/sortIndex';
import type { Task } from '../lib/types';
import { useCustomThemes, useSyncedStore } from '../store/synced';
import { resolveThemeWithLibrary } from '../themes/resolveTheme';
import { playFromSpec } from '../effects/playFromSpec';
import { runTapEffect } from '../effects/tapEffect';

// The kid loop: tap a task → flip its completion → (debounced) IDB write.
// When the toggle finishes the routine, navigate to the celebration screen.
// Per plan/02 §Write strategy + plan/03 §Animations & effects.
export function TaskPage() {
  const { profileId, listId } = useParams();
  const navigate = useNavigate();
  useActiveProfile(profileId);
  const soundOn = useSoundEnabled();

  const tree = useSyncedStore((s) => s.tree);
  const profile = tree?.profiles.find((p) => p.id === profileId);
  // v3: routine lives in the library, referenced via profile.routineIds.
  const routine = profile?.routineIds.includes(listId ?? '')
    ? tree?.routines.find((r) => r.id === listId)
    : undefined;

  const taskCount = routine?.tasks.length ?? 0;
  const { completed, hydrated, toggle, clearAll } = useRoutineProgress(
    profileId ?? '',
    listId ?? '',
    taskCount,
  );

  const shelfRef = useRef<StarShelfHandle>(null);
  const cardRefs = useRef<Array<HTMLElement | null>>([]);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // Close menu on outside click.
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: PointerEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [menuOpen]);

  // Close menu on Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [menuOpen]);

  const customThemes = useCustomThemes();

  if (!profile || !routine) return <Navigate to="/" replace />;

  const tasks = routine.tasks.slice().sort(bySortIndex);
  const doneCount = completed.filter(Boolean).length;
  const rewardEmoji = getRewardEmoji(profile);
  const resolvedTheme = resolveThemeWithLibrary(profile, listId, customThemes);
  const palette = resolvedTheme.routineCardPalette;

  const playTapEffects = (originalIndex: number) => {
    const from = cardRefs.current[originalIndex];
    const to = shelfRef.current?.getSlot(originalIndex) ?? null;
    if (resolvedTheme.customTapSpec && from) {
      runTapEffect(resolvedTheme.customTapSpec, { from, to, rewardEmoji });
      if (soundOn && resolvedTheme.customSoundSpec) {
        playFromSpec(resolvedTheme.customSoundSpec, 'tap');
      }
      return;
    }
    if (soundOn) playDing();
    if (from) spawnSparkles(from);
    if (from && to) flyStar({ from, to, emoji: rewardEmoji });
  };

  const onTap = (originalIndex: number) => {
    const wasDone = completed[originalIndex] === true;
    const next = toggle(originalIndex);
    if (!wasDone) playTapEffects(originalIndex);
    if (next.length > 0 && next.every(Boolean)) {
      navigate(`/p/${profile.id}/celebrate`, { state: { stars: next.length } });
    }
  };

  return (
    <main className="tasks" style={{ ['--profile-color' as string]: profile.color }}>
      <Link
        to={`/p/${profile.id}`}
        className="back-button"
        aria-label="Takaisin rutiinien valintaan"
      >
        <span aria-hidden="true">←</span>
      </Link>
      <div ref={menuRef} className="task-menu-container">
        <button
          type="button"
          className="task-menu-trigger"
          aria-label="Lisätoiminnot"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((o) => !o)}
        >
          ⋮
        </button>
        {menuOpen && (
          <div className="task-menu" role="menu">
            <button
              type="button"
              className="task-menu-item"
              role="menuitem"
              onClick={() => {
                clearAll();
                setMenuOpen(false);
              }}
            >
              Tyhjennä kaikki
            </button>
          </div>
        )}
      </div>
      <header className="tasks-header">
        <h1 className="tasks-title">
          <span aria-hidden="true">{routine.emoji}</span> {routine.name}
        </h1>
        <StarShelf
          ref={shelfRef}
          emoji={rewardEmoji}
          count={taskCount}
          litMask={completed}
          shelfLabel="Tähdet:"
          ready={hydrated}
        />
        <ProgressBar done={doneCount} total={taskCount} />
      </header>
      <ul className="task-list">
        {tasks.map((task, i) => {
          const originalIndex = routine.tasks.indexOf(task);
          const isDone = completed[originalIndex] === true;
          return (
            <li key={task.id}>
              <TaskCard
                task={task}
                done={isDone}
                color={palette[i % palette.length] ?? profile.color}
                onTap={() => onTap(originalIndex)}
                onLongPress={() => onTap(originalIndex)}
                cardRef={(el) => {
                  cardRefs.current[originalIndex] = el;
                }}
              />
            </li>
          );
        })}
      </ul>
    </main>
  );
}

function ProgressBar({ done, total }: { done: number; total: number }) {
  const percent = total === 0 ? 0 : Math.round((done / total) * 100);
  return (
    <div
      className="progress-bar"
      role="progressbar"
      aria-valuenow={done}
      aria-valuemin={0}
      aria-valuemax={total}
      aria-label={`Tehty ${done}/${total}`}
    >
      <div className="progress-bar-fill" style={{ width: `${percent}%` }} />
      <span className="progress-bar-text">
        {done}/{total}
      </span>
    </div>
  );
}

function TaskCard({
  task,
  done,
  color,
  onTap,
  onLongPress,
  cardRef,
}: Readonly<{
  task: Task;
  done: boolean;
  color: string;
  onTap: () => void;
  onLongPress: () => void;
  cardRef: (el: HTMLElement | null) => void;
}>) {
  // A long-press flips done→false mid-gesture; the browser then dispatches the
  // synthetic click that follows pointerup, which would re-select via the
  // newly-active onClick handler. Swallow that one click.
  const longPressFiredRef = useRef(false);
  const { onPointerDown, onPointerUp, onPointerLeave, onPointerCancel, holding } = useLongPress(
    () => {
      longPressFiredRef.current = true;
      onLongPress();
    },
    600,
  );
  const handleClick = () => {
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return;
    }
    if (done) return;
    onTap();
  };
  return (
    <button
      ref={cardRef}
      type="button"
      className={`task-card${done ? ' done' : ''}`}
      style={{ ['--task-color' as string]: color }}
      data-holding={done && holding ? 'true' : undefined}
      onClick={handleClick}
      onPointerDown={done ? onPointerDown : undefined}
      onPointerUp={done ? onPointerUp : undefined}
      onPointerLeave={done ? onPointerLeave : undefined}
      onPointerCancel={done ? onPointerCancel : undefined}
      aria-pressed={done}
    >
      <span className="task-card-emoji" aria-hidden="true">
        {done ? '✅' : task.emoji}
      </span>
      <span className="task-card-body">
        <span className="task-card-name">{task.name}</span>
        {task.tag !== null && <span className="task-card-tag">{task.tag}</span>}
      </span>
    </button>
  );
}
